import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';

import { ACTIONS, type AiAutocompletePluginState } from '../types/types';
import { callHeadingAutocompleteApi } from '../utils/api/headingAutocompleteApi';
import { serializeDocWithCursorMarker } from '../utils/markdownSerializer';

import { acceptSuggestionCommand, dismissSuggestionCommand } from './commands';
import { buildSuggestionDecoration } from './decorations';
import { aiAutocompletePluginKey } from './plugin-key';

export const createPlugin = (): SafePlugin<AiAutocompletePluginState> => {
	let abortController: AbortController | null = null;

	return new SafePlugin({
		key: aiAutocompletePluginKey,

		// -----------------------------------------------------------------------
		// Plugin state — pure synchronous transitions
		// -----------------------------------------------------------------------
		state: {
			init(): AiAutocompletePluginState {
				return { status: 'idle' };
			},

			apply(tr, pluginState): AiAutocompletePluginState {
				const action = tr.getMeta(aiAutocompletePluginKey);
				if (action) {
					switch (action.type) {
						case ACTIONS.TRIGGER_MANUAL:
							return {
								status: 'loading',
								trigger: 'manual',
								cursorPos: action.cursorPos,
							};
						case ACTIONS.SET_SUGGESTION:
							if (pluginState.status === 'loading') {
								return {
									status: 'suggestion',
									trigger: pluginState.trigger,
									cursorPos: pluginState.cursorPos,
									suggestion: action.suggestion,
								};
							}
							return pluginState;
						case ACTIONS.DISMISS:
							return { status: 'idle' };
						default:
							return pluginState;
					}
				}

				if (tr.docChanged && pluginState.status !== 'idle') {
					const state = pluginState;
					return {
						...state,
						...(state.status === 'loading' || state.status === 'suggestion'
							? { cursorPos: tr.mapping.map(state.cursorPos) }
							: {}),
					} as AiAutocompletePluginState;
				}

				return pluginState;
			},
		},

		// -----------------------------------------------------------------------
		// Decorations
		// -----------------------------------------------------------------------
		props: {
			decorations(state: EditorState) {
				const pluginState = aiAutocompletePluginKey.getState(state);
				if (pluginState?.status !== 'suggestion') {
					return null;
				}
				return buildSuggestionDecoration(state, pluginState);
			},

			handleDOMEvents: {
				keydown: (view, event) => {
					const pluginState = aiAutocompletePluginKey.getState(view.state);

					// ── Cmd+Shift+Space — manual inline trigger ──────────────────
					// Works anywhere in the doc, fires immediately with no debounce.
					if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.code === 'Space') {
						event.preventDefault();

						// Get current cursor position and heading context
						const { from: resolvedCursorPos } = view.state.selection;

						// Dismiss any existing suggestion first
						if (pluginState && pluginState.status !== 'idle') {
							dismissSuggestionCommand(view.state, view.dispatch);
						}

						view.dispatch(
							view.state.tr.setMeta(aiAutocompletePluginKey, {
								type: ACTIONS.TRIGGER_MANUAL,
								cursorPos: resolvedCursorPos,
							}),
						);
						return true;
					} else {
						// ── Tab — accept manual inline suggestion ─────────────────────
						if (event.key === 'Tab') {
							if (pluginState?.status !== 'suggestion' || pluginState.trigger !== 'manual') {
								return false;
							}
							event.preventDefault();
							return acceptSuggestionCommand(view.state, view.dispatch);
						}

						// ── Escape — dismiss any suggestion ───────────────────────────
						if (event.key === 'Escape') {
							if (!pluginState || pluginState.status === 'idle') {
								return false;
							}
							return dismissSuggestionCommand(view.state, view.dispatch);
						}

						if (!pluginState || pluginState.status === 'idle') {
							return false;
						}
						// Typing anything else — dismiss any suggestion
						return dismissSuggestionCommand(view.state, view.dispatch);
					}
				},

				blur: (view) => {
					const pluginState = aiAutocompletePluginKey.getState(view.state);
					if (pluginState && pluginState.status !== 'idle') {
						dismissSuggestionCommand(view.state, view.dispatch);
					}
					return false;
				},
			},
		},

		// -----------------------------------------------------------------------
		// view() — owns all side effects (debounce timer + fetch lifecycle)
		// -----------------------------------------------------------------------
		view() {
			return {
				update(view, prevState) {
					const newPluginState = aiAutocompletePluginKey.getState(view.state);
					const prevPluginState = aiAutocompletePluginKey.getState(prevState);

					if (newPluginState === prevPluginState) {
						return;
					}

					// Leaving 'loading' — cancel in-flight fetch
					if (prevPluginState?.status === 'loading' && newPluginState?.status !== 'loading') {
						abortController?.abort();
						abortController = null;
					}

					// Entering 'loading' — fire the API call
					if (newPluginState?.status === 'loading') {
						abortController = new AbortController();

						const markdown = serializeDocWithCursorMarker(view, newPluginState.cursorPos);

						callHeadingAutocompleteApi(markdown, abortController.signal)
							.then((suggestion) => {
								view.dispatch(
									view.state.tr.setMeta(aiAutocompletePluginKey, {
										type: ACTIONS.SET_SUGGESTION,
										suggestion,
									}),
								);
							})
							.catch((err: unknown) => {
								if (err instanceof Error && err.name === 'AbortError') {
									return;
								}
								view.dispatch(
									view.state.tr.setMeta(aiAutocompletePluginKey, {
										type: ACTIONS.DISMISS,
									}),
								);
							});
					}
				},

				destroy() {
					abortController?.abort();
					abortController = null;
				},
			};
		},

		// -----------------------------------------------------------------------
		// appendTransaction — detects heading edits and cursor movement
		// -----------------------------------------------------------------------
		appendTransaction(transactions, _oldState, newState) {
			const currentPluginState = aiAutocompletePluginKey.getState(newState);

			// For manual suggestions, don't auto-dismiss on cursor movement —
			// the user may move the cursor while reviewing the suggestion.
			// We only dismiss on Escape or blur.
			if (currentPluginState?.status === 'suggestion' && currentPluginState.trigger === 'manual') {
				return null;
			}

			const docChanged = transactions.some((tr) => tr.docChanged);
			if (!docChanged) {
				return null;
			}

			return newState.tr.setMeta(aiAutocompletePluginKey, {
				status: 'idle',
			});
		},
	});
};
